import type { DocumentData } from "firebase/firestore";
import { Timestamp } from "firebase/firestore";
import {
  MessageAttachments,
  createMessage,
  type Message,
  type MessageAttachment,
  type MessageRole,
  type ToolCall,
} from "axon-provider-kit/core";
import { compactData, dateValue, stringValue } from "./firestore";
import type { SBotActiveContext, SBotMembershipTargetType, SBotUserRole } from "./user";

// Mirrors Models/SBotChatModels.swift + the SBotChatCloudMapper Firestore
// shapes in Services/SBotChatCloudSyncService.swift. firestore.rules validates
// these exact field sets — keep in sync with the rules and the Swift models.

export type SBotChatRole = "user" | "assistant" | "tool";

export type SBotEffortLevel = "quick" | "standard" | "extended";

export const DEFAULT_CHAT_EFFORT: SBotEffortLevel = "quick";

export const EFFORT_LEVELS: SBotEffortLevel[] = ["quick", "standard", "extended"];

export const EFFORT_LABELS: Record<SBotEffortLevel, { title: string; subtitle: string }> = {
  quick: { title: "Quick", subtitle: "Fastest answers" },
  standard: { title: "Standard", subtitle: "Best for most questions" },
  extended: { title: "Extended", subtitle: "Complex problem solving" },
};

/** Mirrors SBotEffortLevel.openAIReasoningEffort. */
export const EFFORT_TO_OPENAI_REASONING: Record<SBotEffortLevel, "low" | "medium" | "high"> = {
  quick: "low",
  standard: "medium",
  extended: "high",
};

export interface SBotChatWorkspaceScope {
  targetType: SBotMembershipTargetType;
  targetID: string;
  role: SBotUserRole;
  displayName: string;
  leagueID?: string;
  organizationID?: string;
  sportID?: string;
  storageKey: string;
}

export interface SBotChatAttachment {
  id: string;
  name: string;
  mimeType: string;
  kind: "image" | "document";
  byteCount: number;
  /** Raw base64 payload; local/runtime only — Firestore stores metadata + storagePath. */
  base64Data: string;
}

export interface SBotChatToolCall {
  id: string;
  name: string;
  arguments: string;
  thoughtSignature?: string;
}

export interface SBotChatMessage {
  id: string;
  role: SBotChatRole;
  content: string;
  attachments: SBotChatAttachment[];
  createdAt: Date;
  modelID?: string;
  modelName?: string;
  providerID?: string;
  personalityID?: string;
  personalityName?: string;
  reasoning?: string;
  toolCalls: SBotChatToolCall[];
  toolCallID?: string;
  toolName?: string;
  isToolError: boolean;
  /** Local/cloud presentation metadata. Never forwarded to the model provider. */
  approvalPresentationAfterMessageID?: string;
}

export interface SBotChatConversation {
  id: string;
  title: string;
  createdAt: Date;
  updatedAt: Date;
  workspaceScope?: SBotChatWorkspaceScope;
  selectedPersonalityID?: string;
  selectedModelID: string;
  selectedEffort: SBotEffortLevel;
  messages: SBotChatMessage[];
  smartTitleGeneratedAt?: Date;
}

/** Uppercase UUIDs match Swift's UUID().uuidString, keeping doc IDs consistent across clients. */
export function chatUUID(): string {
  return crypto.randomUUID().toUpperCase();
}

// MARK: - Workspace scope

export function workspaceStorageKey(targetType: SBotMembershipTargetType, targetID: string, role: SBotUserRole): string {
  return sanitizedStorageKey(`workspace-${targetType}-${targetID}-${role}`);
}

/** Mirrors SBotChatWorkspaceScope.sanitized: alphanumerics + "-_", others → "-", trimmed of "-". */
export function sanitizedStorageKey(raw: string): string {
  const mapped = [...raw].map((char) => (/[A-Za-z0-9_-]/.test(char) ? char : "-")).join("");
  const trimmed = mapped.replace(/^-+/, "").replace(/-+$/, "");
  return trimmed.length > 0 ? trimmed : "workspace";
}

export function workspaceScopeFromActiveContext(context: SBotActiveContext): SBotChatWorkspaceScope {
  return {
    targetType: context.targetType,
    targetID: context.targetID,
    role: context.role,
    displayName: context.displayName,
    leagueID: context.leagueID ?? (context.targetType === "league" ? context.targetID : undefined),
    organizationID: context.organizationID ?? (context.targetType === "organization" ? context.targetID : undefined),
    sportID: context.sportID,
    storageKey: workspaceStorageKey(context.targetType, context.targetID, context.role),
  };
}

export function workspaceScopeData(scope: SBotChatWorkspaceScope): Record<string, unknown> {
  return compactData({
    targetType: scope.targetType,
    targetID: scope.targetID,
    role: scope.role,
    displayName: scope.displayName,
    storageKey: scope.storageKey,
    leagueID: scope.leagueID,
    organizationID: scope.organizationID,
    sportID: scope.sportID,
  });
}

export function parseWorkspaceScope(data: unknown): SBotChatWorkspaceScope | undefined {
  if (typeof data !== "object" || data === null) return undefined;
  const record = data as DocumentData;
  const targetType = stringValue(record.targetType);
  const targetID = stringValue(record.targetID);
  const role = stringValue(record.role);
  const displayName = stringValue(record.displayName);
  if (!targetType || !targetID || !role || displayName === undefined) return undefined;
  if (targetType !== "league" && targetType !== "organization") return undefined;
  return {
    targetType,
    targetID,
    role: role as SBotUserRole,
    displayName,
    leagueID: stringValue(record.leagueID),
    organizationID: stringValue(record.organizationID),
    sportID: stringValue(record.sportID),
    storageKey: stringValue(record.storageKey) ?? workspaceStorageKey(targetType, targetID, role as SBotUserRole),
  };
}

// MARK: - Conversation (de)serialization — SBotChatCloudMapper.conversationData/conversation

export const DEFAULT_CHAT_MODEL_ID = "gpt-5.5";
export const NEW_CHAT_TITLE = "New chat";

export function createChatConversation(init: Partial<SBotChatConversation> & { selectedModelID: string }): SBotChatConversation {
  const now = new Date();
  return {
    id: init.id ?? chatUUID(),
    title: init.title ?? NEW_CHAT_TITLE,
    createdAt: init.createdAt ?? now,
    updatedAt: init.updatedAt ?? now,
    workspaceScope: init.workspaceScope,
    selectedPersonalityID: init.selectedPersonalityID,
    selectedModelID: init.selectedModelID,
    selectedEffort: init.selectedEffort ?? DEFAULT_CHAT_EFFORT,
    messages: init.messages ?? [],
    smartTitleGeneratedAt: init.smartTitleGeneratedAt,
  };
}

export function conversationFirestoreData(conversation: SBotChatConversation, deletedAt?: Date): Record<string, unknown> {
  return compactData({
    id: conversation.id,
    title: conversation.title,
    createdAt: Timestamp.fromDate(conversation.createdAt),
    updatedAt: Timestamp.fromDate(conversation.updatedAt),
    selectedModelID: conversation.selectedModelID,
    selectedEffort: conversation.selectedEffort,
    messageCount: conversation.messages.length,
    selectedPersonalityID: conversation.selectedPersonalityID,
    workspaceScope: conversation.workspaceScope ? workspaceScopeData(conversation.workspaceScope) : undefined,
    smartTitleGeneratedAt: conversation.smartTitleGeneratedAt ? Timestamp.fromDate(conversation.smartTitleGeneratedAt) : undefined,
    deletedAt: deletedAt ? Timestamp.fromDate(deletedAt) : undefined,
  });
}

/** Returns undefined for soft-deleted or malformed docs (mirrors SBotChatCloudMapper.conversation). */
export function parseChatConversation(id: string, data: DocumentData, messages: SBotChatMessage[]): SBotChatConversation | undefined {
  if (dateValue(data.deletedAt)) return undefined;
  const conversationID = stringValue(data.id) ?? id;
  const selectedEffortRaw = stringValue(data.selectedEffort);
  const selectedEffort: SBotEffortLevel = selectedEffortRaw === "quick" || selectedEffortRaw === "standard" || selectedEffortRaw === "extended" ? selectedEffortRaw : DEFAULT_CHAT_EFFORT;

  return {
    id: conversationID,
    title: stringValue(data.title) ?? NEW_CHAT_TITLE,
    createdAt: dateValue(data.createdAt) ?? new Date(),
    updatedAt: dateValue(data.updatedAt) ?? new Date(),
    workspaceScope: parseWorkspaceScope(data.workspaceScope),
    selectedPersonalityID: stringValue(data.selectedPersonalityID),
    selectedModelID: stringValue(data.selectedModelID) ?? DEFAULT_CHAT_MODEL_ID,
    selectedEffort,
    messages: [...messages].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()),
    smartTitleGeneratedAt: dateValue(data.smartTitleGeneratedAt),
  };
}

// MARK: - Message (de)serialization — SBotChatCloudMapper.messageData/message

export function attachmentStoragePath(
  userID: string,
  workspaceScope: SBotChatWorkspaceScope | undefined,
  conversationID: string,
  messageID: string,
  attachmentID: string,
): string {
  if (workspaceScope) {
    return `chatAttachments/${userID}/workspaces/${workspaceScope.storageKey}/${conversationID}/${messageID}/${attachmentID}`;
  }
  return `chatAttachments/${userID}/${conversationID}/${messageID}/${attachmentID}`;
}

export function messageFirestoreData(
  message: SBotChatMessage,
  context: { userID: string; conversationID: string; workspaceScope?: SBotChatWorkspaceScope },
): Record<string, unknown> {
  return compactData({
    id: message.id,
    role: message.role,
    content: message.content,
    attachments: message.attachments.map((attachment) =>
      compactData({
        id: attachment.id,
        name: attachment.name,
        mimeType: attachment.mimeType,
        kind: attachment.kind,
        byteCount: attachment.byteCount,
        storagePath: attachmentStoragePath(context.userID, context.workspaceScope, context.conversationID, message.id, attachment.id),
      }),
    ),
    createdAt: Timestamp.fromDate(message.createdAt),
    toolCalls: message.toolCalls.map((toolCall) =>
      compactData({
        id: toolCall.id,
        name: toolCall.name,
        arguments: toolCall.arguments,
        thoughtSignature: toolCall.thoughtSignature,
      }),
    ),
    isToolError: message.isToolError,
    modelID: message.modelID,
    modelName: message.modelName,
    providerID: message.providerID,
    personalityID: message.personalityID,
    personalityName: message.personalityName,
    reasoning: message.reasoning,
    toolCallID: message.toolCallID,
    toolName: message.toolName,
    approvalPresentationAfterMessageID: message.approvalPresentationAfterMessageID,
  });
}

export type AttachmentLoader = (storagePath: string, byteCount: number) => Promise<string>;

/** Returns undefined for soft-deleted docs. Attachment base64 loads lazily via the loader. */
export async function parseChatMessage(id: string, data: DocumentData, loadAttachment: AttachmentLoader): Promise<SBotChatMessage | undefined> {
  if (dateValue(data.deletedAt)) return undefined;
  const messageID = stringValue(data.id) ?? id;
  const roleRaw = stringValue(data.role);
  const role: SBotChatRole = roleRaw === "user" || roleRaw === "tool" ? roleRaw : "assistant";

  const attachments: SBotChatAttachment[] = [];
  for (const item of Array.isArray(data.attachments) ? data.attachments : []) {
    const attachment = await parseAttachment(item, loadAttachment);
    if (attachment) attachments.push(attachment);
  }

  const toolCalls: SBotChatToolCall[] = (Array.isArray(data.toolCalls) ? data.toolCalls : [])
    .map(parseToolCall)
    .filter((value): value is SBotChatToolCall => value !== undefined);

  return {
    id: messageID,
    role,
    content: stringValue(data.content) ?? "",
    attachments,
    createdAt: dateValue(data.createdAt) ?? new Date(),
    modelID: stringValue(data.modelID),
    modelName: stringValue(data.modelName),
    providerID: stringValue(data.providerID),
    personalityID: stringValue(data.personalityID),
    personalityName: stringValue(data.personalityName),
    reasoning: stringValue(data.reasoning),
    toolCalls,
    toolCallID: stringValue(data.toolCallID),
    toolName: stringValue(data.toolName),
    isToolError: typeof data.isToolError === "boolean" ? data.isToolError : false,
    approvalPresentationAfterMessageID: stringValue(data.approvalPresentationAfterMessageID),
  };
}

async function parseAttachment(data: unknown, loadAttachment: AttachmentLoader): Promise<SBotChatAttachment | undefined> {
  if (typeof data !== "object" || data === null) return undefined;
  const record = data as DocumentData;
  const id = stringValue(record.id);
  if (!id) return undefined;

  const byteCount = typeof record.byteCount === "number" ? record.byteCount : 0;
  const storagePath = stringValue(record.storagePath);
  let base64Data: string;
  const inline = stringValue(record.base64Data);
  if (inline !== undefined) {
    base64Data = inline;
  } else if (storagePath) {
    base64Data = await loadAttachment(storagePath, Math.max(byteCount, 1));
  } else {
    return undefined;
  }

  const kindRaw = stringValue(record.kind);
  return {
    id,
    name: stringValue(record.name) ?? "Attachment",
    mimeType: stringValue(record.mimeType) ?? "application/octet-stream",
    kind: kindRaw === "image" ? "image" : "document",
    byteCount,
    base64Data,
  };
}

function parseToolCall(data: unknown): SBotChatToolCall | undefined {
  if (typeof data !== "object" || data === null) return undefined;
  const record = data as DocumentData;
  const id = stringValue(record.id);
  const name = stringValue(record.name);
  const argumentsValue = stringValue(record.arguments);
  if (!id || !name || argumentsValue === undefined) return undefined;
  return { id, name, arguments: argumentsValue, thoughtSignature: stringValue(record.thoughtSignature) };
}

// MARK: - Provider message conversion — SBotChatMessage.providerMessage / init(providerMessage:)

export function chatMessageToProviderMessage(message: SBotChatMessage): Message {
  const providerRole: MessageRole = message.role;
  // Attachments whose bytes could not be loaded are dropped rather than sent as an
  // empty image/document payload (see SBotChatSyncService.downloadAttachmentBase64).
  const loadedAttachments = message.attachments.filter((attachment) => attachment.base64Data.length > 0);
  return createMessage({
    id: message.id,
    role: providerRole,
    content: message.content,
    timestamp: message.createdAt,
    ...(message.modelName !== undefined && { modelName: message.modelName }),
    ...(loadedAttachments.length > 0 && { attachments: loadedAttachments.map(toProviderAttachment) }),
    ...(message.reasoning !== undefined && { reasoning: message.reasoning }),
    ...(message.toolCalls.length > 0 && {
      toolCalls: message.toolCalls.map(
        (toolCall): ToolCall => ({
          id: toolCall.id,
          name: toolCall.name,
          arguments: toolCall.arguments,
          ...(toolCall.thoughtSignature !== undefined && { thoughtSignature: toolCall.thoughtSignature }),
        }),
      ),
    }),
    ...(message.toolCallID !== undefined && { toolCallId: message.toolCallID }),
    ...(message.toolName !== undefined && { toolName: message.toolName }),
  });
}

export interface ProviderMessageFallbacks {
  modelID?: string;
  modelName?: string;
  providerID?: string;
  personalityID?: string;
  personalityName?: string;
}

export function providerMessageToChatMessage(message: Message, fallbacks: ProviderMessageFallbacks = {}): SBotChatMessage {
  const role: SBotChatRole = message.role === "user" ? "user" : message.role === "tool" ? "tool" : "assistant";
  return {
    id: normalizedMessageID(message.id),
    role,
    content: message.content,
    attachments: (message.attachments ?? [])
      .map(fromProviderAttachment)
      .filter((value): value is SBotChatAttachment => value !== undefined),
    createdAt: message.timestamp,
    modelID: fallbacks.modelID,
    modelName: message.modelName ?? fallbacks.modelName,
    providerID: fallbacks.providerID,
    personalityID: fallbacks.personalityID,
    personalityName: fallbacks.personalityName,
    reasoning: message.reasoning,
    toolCalls: (message.toolCalls ?? []).map((toolCall) => ({
      id: toolCall.id,
      name: toolCall.name,
      arguments: toolCall.arguments,
      thoughtSignature: toolCall.thoughtSignature,
    })),
    toolCallID: message.toolCallId,
    toolName: message.toolName,
    isToolError: isErrorToolPayload(message.content),
  };
}

function normalizedMessageID(id: string): string {
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(id) ? id.toUpperCase() : chatUUID();
}

export function toProviderAttachment(attachment: SBotChatAttachment): MessageAttachment {
  if (attachment.kind === "image") {
    return MessageAttachments.imageBase64(attachment.base64Data, attachment.mimeType);
  }
  return MessageAttachments.document(attachment.base64Data, attachment.name, attachment.mimeType);
}

function fromProviderAttachment(attachment: MessageAttachment): SBotChatAttachment | undefined {
  if (!attachment.base64) return undefined;
  const kind = attachment.type === "image" ? "image" : "document";
  return {
    id: normalizedMessageID(attachment.id),
    name: attachment.name ?? (kind === "image" ? "Image" : "Attachment"),
    mimeType: attachment.mimeType ?? "application/octet-stream",
    kind,
    byteCount: estimatedBase64Bytes(attachment.base64),
    base64Data: attachment.base64,
  };
}

function estimatedBase64Bytes(base64: string): number {
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

/** Best-effort port of SBotToolResultEnvelope.isErrorPayload (full envelope arrives with the tool registry). */
export function isErrorToolPayload(content: string): boolean {
  const trimmed = content.trim();
  if (!trimmed.startsWith("{")) return false;
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    if (parsed.ok === "false" || parsed.ok === false) return true;
    return typeof parsed.error === "string" && parsed.error.length > 0;
  } catch {
    return false;
  }
}

// MARK: - Conversation helpers (SBotChatConversation mutations)

export function conversationPreviewText(conversation: SBotChatConversation): string {
  const last = [...conversation.messages].reverse().find((message) => message.content.trim().length > 0);
  return last?.content ?? "No messages yet";
}

/** Mirrors SBotChatConversation.refreshTitleIfNeeded. Returns the updated title. */
export function refreshedTitle(conversation: SBotChatConversation, force = false): string {
  if (conversation.smartTitleGeneratedAt) return conversation.title;
  if (!force && conversation.title !== NEW_CHAT_TITLE) return conversation.title;

  const firstUserMessage = conversation.messages.find((message) => message.role === "user");
  if (firstUserMessage) {
    const trimmed = firstUserMessage.content.trim();
    if (trimmed.length > 0) return trimmed.slice(0, 64);
    if (firstUserMessage.attachments.length > 0) {
      return firstUserMessage.attachments.length === 1 ? firstUserMessage.attachments[0].name : `${firstUserMessage.attachments.length} attachments`;
    }
  }
  return NEW_CHAT_TITLE;
}
